import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Spinner, Alert, Form, Row, Col, ListGroup } from 'react-bootstrap';
import axios from 'axios';
import EventSpecificationForm from './EventSpecificationForm';
import { EventSpecification, QueryParams, FullEquipment } from '../interfaces';
import MessageModal from './MessageModal';
import ConfirmationModal from './ConfirmationModal';
import EquipmentDetailModal from './EquipmentDetailModal';
// אייקונים
import { 
  faPlus, 
  faEdit, 
  faTrash 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';

import { API_BASE_URL, buildApiUrl } from '../config/api';

const formatCasesLabel = (quantity: number, caseQty?: number) => {
  const cq = Number(caseQty || 1);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(cq) || cq <= 1) return null;

  const rawCases = quantity / cq;
  const isInt = Number.isInteger(rawCases);
  const casesText = isInt ? String(rawCases) : rawCases.toFixed(2);
  const noun = rawCases === 1 ? 'קייס' : 'קייסים';
  return `${casesText} ${noun}`;
};

const EventSpecificationList: React.FC = () => {
  const { user } = useAuth();
  const [specifications, setSpecifications] = useState<EventSpecification[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [specificationToEdit, setSpecificationToEdit] = useState<EventSpecification | null>(null);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('name');

  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [messageModalTitle, setMessageModalTitle] = useState<string>('');
  const [messageModalContent, setMessageModalContent] = useState<string>('');
  const [messageModalVariant, setMessageModalVariant] = useState<'success' | 'danger' | 'info' | 'warning'>('info');

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>('');

  // Modal לפרטי ציוד
  const [showEquipmentDetailModal, setShowEquipmentDetailModal] = useState<boolean>(false);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<FullEquipment | null>(null);
  const [allEquipment, setAllEquipment] = useState<FullEquipment[]>([]);

  const fetchSpecifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: QueryParams = {};
      if (searchQuery) params.search = searchQuery;
      if (sortBy) params.ordering = sortBy;

      const [specificationsRes, equipmentRes] = await Promise.all([
        axios.get(buildApiUrl('events/event-specifications/'), { params }),
        axios.get(buildApiUrl('inventory/equipment/'))
      ]);
      setSpecifications(specificationsRes.data);
      setAllEquipment(equipmentRes.data);
    } catch (err) {
      console.error("שגיאה בטעינת מפרטים:", err);
      setError("לא ניתן לטעון מפרטים.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchSpecifications();
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery, sortBy]);

  const showMessage = (title: string, message: string, variant: 'success' | 'danger' | 'info' | 'warning' = 'info') => {
    setMessageModalTitle(title);
    setMessageModalContent(message);
    setMessageModalVariant(variant);
    setShowMessageModal(true);
  };

  const handleAddClick = () => {
    setSpecificationToEdit(null);
    setShowForm(true);
  };

  const handleEditClick = (spec: EventSpecification) => {
    setSpecificationToEdit(spec);
    setShowForm(true);
  };

  const handleDeleteClick = (id: number) => {
    setConfirmModalTitle('אישור מחיקה');
    setConfirmModalMessage('האם אתה בטוח שברצונך למחוק מפרט זה?');
    setConfirmAction(() => async () => {
      try {
        const spec = specifications.find(s => s.id === id);
        await axios.delete(`${buildApiUrl('events/event-specifications/')}id}/`);
        showMessage('הצלחה', `המפרט ${spec ? `"${spec.name}" ` : ''}נמחק בהצלחה!`, 'success');
        fetchSpecifications();
      } catch (err) {
        showMessage('שגיאה', 'שגיאה במחיקת המפרט.', 'danger');
      }
    });
    setShowConfirmModal(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    fetchSpecifications();
    showMessage('הצלחה', specificationToEdit ? `המפרט "${specificationToEdit.name}" עודכן בהצלחה!` : 'מפרט חדש נוצר בהצלחה!', 'success');
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setSpecificationToEdit(null);
  };

  // פונקציה לטיפול בלחיצה על פריט ציוד
  const handleEquipmentItemClick = (equipmentId: number) => {
    const fullEquipment = allEquipment.find(eq => eq.id === equipmentId);
    if (fullEquipment) {
      setSelectedEquipmentForDetails(fullEquipment);
      setShowEquipmentDetailModal(true);
    } else {
      showMessage('שגיאה', 'לא ניתן למצוא פרטי ציוד מלאים.', 'danger');
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
        <h2>מפרטי אירועים</h2>
        <Button variant="success" onClick={handleAddClick}>
          <FontAwesomeIcon icon={faPlus} className="me-1" />
          הוסף מפרט חדש
        </Button>
      </div>

      <Form className="mb-4 p-3 border rounded bg-light">
        <Row>
          <Col md={12}>
            <Form.Group controlId="searchQuery">
              <Form.Label>חיפוש</Form.Label>
              <Form.Control
                type="text"
                placeholder="חפש לפי שם, תיאור..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && (
        !specifications || specifications.length === 0 ? (
          <p>אין מפרטים במערכת.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>שם מפרט</th>
                <th>תיאור</th>
                <th>פריטי ציוד</th>
                <th>פריטים מתכלים</th>
                <th>ציוד מושכר</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {specifications.map(spec => (
                <tr key={spec.id}>
                  <td>{spec.name}</td>
                  <td>{spec.description || 'אין'}</td>
                  <td>
                    {spec.items && spec.items.length > 0 ? (
                      <ListGroup variant="flush">
                        {spec.items.map(item => (
                          (() => {
                            const inv = allEquipment.find(eq => eq.id === item.equipment);
                            const casesLabel = formatCasesLabel(Number(item.quantity || 0), inv?.case_quantity);
                            return (
                          <ListGroup.Item 
                            key={item.id || `${spec.id}-${item.equipment}`}
                            onClick={() => handleEquipmentItemClick(item.equipment!)}
                            style={{ cursor: 'pointer' }}
                            title="לחץ לצפייה בפרטי הציוד"
                          >
                            {item.quantity}x {item.equipment_name}
                            {casesLabel && (
                              <small className="d-block text-muted">
                                ({casesLabel})
                              </small>
                            )}
                          </ListGroup.Item>
                            );
                          })()
                        ))}
                      </ListGroup>
                    ) : (<span>אין</span>)}
                  </td>
                  <td>
                    {spec.consumables && spec.consumables.length > 0 ? (
                      <ListGroup variant="flush">
                        {spec.consumables.map(item => (
                          <ListGroup.Item key={item.id || `${spec.id}-${item.consumable}`}>
                            {item.quantity}x {item.consumable_name}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (<span>אין</span>)}
                  </td>
                  <td>
                    {spec.rented_items && spec.rented_items.length > 0 ? (
                      <ListGroup variant="flush">
                        {spec.rented_items.map(item => (
                          <ListGroup.Item key={item.id || `${spec.id}-rented-${item.name}`}>
                            {item.quantity}x {item.name} ({item.supplier_name})
                            {item.arrival_date && (
                              <small className="d-block text-muted">
                                הגעה: {new Date(item.arrival_date).toLocaleDateString('he-IL')}
                              </small>
                            )}
                            {item.departure_date && (
                              <small className="d-block text-muted">
                                עזיבה: {new Date(item.departure_date).toLocaleDateString('he-IL')}
                              </small>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (<span>אין</span>)}
                  </td>
                  <td>
                    <Button variant="warning" size="sm" className="me-2" onClick={() => handleEditClick(spec)}>
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      ערוך
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDeleteClick(spec.id!)} disabled={user?.role?.name === 'WAREHOUSE'} title={user?.role?.name === 'WAREHOUSE' ? 'למחסנאי אין הרשאה למחיקה' : ''}>
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                      מחק
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )
      )}

      <Modal show={showForm} onHide={handleFormCancel} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{specificationToEdit ? 'עריכת מפרט' : 'הוספת מפרט'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EventSpecificationForm
            specificationToEdit={specificationToEdit}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </Modal.Body>
      </Modal>

      <MessageModal show={showMessageModal} onHide={() => setShowMessageModal(false)} title={messageModalTitle} message={messageModalContent} variant={messageModalVariant} />
      <ConfirmationModal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} title={confirmModalTitle} message={confirmModalMessage} onConfirm={confirmAction || (() => {})} />
      
      {/* מודל פרטי ציוד */}
      <EquipmentDetailModal 
        show={showEquipmentDetailModal} 
        onHide={() => setShowEquipmentDetailModal(false)} 
        equipment={selectedEquipmentForDetails} 
        allEquipment={allEquipment} 
      />
    </div>
  );
};

export default EventSpecificationList;
